// The partner-brand wizard BODY — stepper, step content, and footer. Extracted
// from PartnerBrandCreateWizard so the route (/partners/brands/new, which the
// Partner Brands list still needs) and the in-record popup
// (PartnerBrandWizardModal) render exactly the same thing. There is deliberately
// no second copy of any step: the two entry points differ only in their chrome
// (page header vs modal frame) and in what "cancel" and "finished" mean, both of
// which arrive as callbacks.
//
// Step 1 persists a REAL brand row inactive (activate:false, so it is invisible
// on the public funnel), captures the id, and later steps update that same row;
// the final step flips it live. The logo / colors / contact field groups are
// shared verbatim with PartnerBrandEditPage so the surfaces never drift.
//
// Launch context: the wizard can be launched pre-owned from a Broker or
// Brokerage record (NewDealWizard's initialBrokerId precedent). The owner stays
// changeable on step 1, and the standalone ownerless path is unchanged.
//
// Context fork: a BROKER launch first asks who the site is for, because a broker
// under a firm can be given the firm's shared site or a private one of their own
// and the difference is invisible afterwards (a broker's own site is private
// from their firm in the portal). The gate renders IN PLACE OF step 1's body, so
// a brokerage launch and the standalone path never see it and behave exactly as
// before. stepIndex stays numeric and the steps are NOT renumbered.
//
// Brand-guide pre-step: CREATE only, a second gate rendered IN PLACE OF step
// 1's body once the context fork (if any) is answered. It has to come before
// Identity rather than beside it, because prefill only ever fills fields nobody
// has touched — offered later it would have almost nothing left to fill. It
// needs no brand id (nothing is persisted), skipping is an ordinary path, and a
// failed read continues exactly as a skip does. stepIndex stays numeric and the
// steps are NOT renumbered.
//
// EDIT mode reuses the same steps against an existing brand: the row already
// exists, so step 1 updates instead of creating, the slug is fixed, there is no
// context fork, and the terminal action saves rather than activating.

import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import type {
  CreatePartnerBrandRequest,
  PartnerBrand,
  PartnerBrandColors,
  PartnerBrandKitResult,
  PartnerBrandValidationMessage,
  PartnerLogoMode,
  UpdatePartnerBrandRequest,
} from "@/api/partnerBrands";
import { partnerBrandsApi } from "@/api/partnerBrands";
import {
  useCreatePartnerBrand,
  usePartnerBrand,
  usePartnerBrands,
  useSetPartnerBrandActive,
  useUpdatePartnerBrand,
} from "@/hooks/partners/usePartnerBrands";
import {
  Badge,
  Button,
  Dropzone,
  Input,
  Label,
  Textarea,
} from "@/design-system/components/ui";
import {
  StatusStepper,
  type StepperStep,
} from "@/design-system/components/ui/StatusStepper";
import { useToast } from "@/components/ui/Toast";
import { extractErrorMessage } from "@/api/client";
import {
  COLOR_FIELDS,
  DEFAULT_COLORS,
  DEFAULT_FONT_BODY,
  DEFAULT_FONT_DISPLAY,
  asSlugConflict,
  asValidationResult,
  swatch,
  toSlug,
} from "./shared";
import { PartnerBrandColorsSection } from "./PartnerBrandColorsSection";
import { PartnerBrandExtractedMarker } from "./PartnerBrandExtractedMarker";
import { PartnerBrandContactSection } from "./PartnerBrandContactSection";
import { PartnerBrandLogoControl } from "./PartnerBrandLogoControl";
import {
  OWNERLESS,
  PartnerBrandOwnerSection,
  ownerFromBrand,
  ownerIds,
  type PartnerBrandOwner,
} from "./PartnerBrandOwnerSection";
import {
  AMBIGUOUS_PERSONAL,
  PartnerBrandContextDecision,
  PartnerBrandContextLoading,
  PartnerBrandContextNotice,
  defaultChoiceFor,
  type PartnerBrandContextChoice,
} from "./PartnerBrandContextDecision";
import {
  decisionNeedsChoice,
  usePartnerBrandLaunchDecision,
} from "@/hooks/partners/usePartnerBrandLaunchDecision";
import { useConfirmPrequalSiteInheritance } from "@/hooks/partners/usePrequalSiteInherit";

const STEPS: StepperStep[] = [
  { key: "identity", label: "Identity" },
  { key: "logo", label: "Logo" },
  { key: "brand", label: "Colors & fonts" },
  { key: "content", label: "Hero & contact" },
  { key: "review", label: "Review & activate" },
];
const LAST = STEPS.length - 1;

function ReviewRow({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-space-1 sm:flex-row sm:items-baseline sm:gap-space-3">
      <span className="text-caption text-gray-mid font-display uppercase tracking-wider sm:w-40 shrink-0">
        {label}
      </span>
      <span className="text-small text-onyx min-w-0">{children}</span>
    </div>
  );
}

/** What a host needs to decide whether closing loses anything. */
export interface PartnerBrandWizardState {
  /** Something is entered that a close would throw away. */
  dirty: boolean;
  /** Create mode: the inactive draft row that already exists server-side, if
   *  any. Closing leaves it behind in the Partner Brands list. */
  draftBrandId: string | null;
  /** A request is in flight (a step persist, the inheritance stamp, or a brand
   *  guide being read). A host must not tear the wizard down mid-flight. */
  busy: boolean;
}

/** 10MB — the server's cap. Stated here so the Dropzone rejects an oversize
 *  file locally with the same limit rather than after a 45-second upload. */
const BRAND_KIT_MAX_BYTES = 10 * 1024 * 1024;

/** The brand-guide upload, rendered in place of Identity. One screen, one
 *  action, and a way past it that is not an error — a client with no brand
 *  guide must still get a site. */
function BrandKitStep({
  extracting,
  result,
  error,
  onFile,
}: {
  extracting: boolean;
  result: PartnerBrandKitResult | null;
  error: string | null;
  onFile: (file: File) => void;
}) {
  return (
    <section
      className="flex flex-col gap-space-3"
      data-testid="partner-brand-kit-step"
    >
      <h2 className="font-display font-bold text-h3 m-0">Brand guide</h2>
      <p className="text-small text-gray-mid m-0 max-w-reading">
        Upload the partner&rsquo;s brand guide as a PDF and we will read the
        colors, fonts, hero copy and contact details out of it to start from.
        Anything we read is marked as you go, so you can check it before the site
        goes live. Select Skip to enter everything by hand instead.
      </p>

      <Dropzone
        onFiles={(files) => {
          if (files[0]) onFile(files[0]);
        }}
        accept=".pdf,application/pdf"
        maxSizeBytes={BRAND_KIT_MAX_BYTES}
        multiple={false}
        disabled={extracting}
        hint="PDF, up to 10 MB. Nothing is saved until you finish the wizard."
        data-testid="partner-brand-kit-dropzone"
        inputTestId="partner-brand-kit-input"
      />

      {extracting && (
        <p
          className="text-small text-gray-mid m-0"
          data-testid="partner-brand-kit-extracting"
        >
          Reading the brand guide — this can take up to a minute.
        </p>
      )}

      {/* The server's message already distinguishes "read it, found nothing"
          from "could not read it", and names any colors it dropped. Rendering
          it verbatim keeps the two from collapsing into one vague sentence. */}
      {!extracting && result && (
        <p
          className={
            result.outcome === "Succeeded"
              ? "text-small text-onyx m-0 max-w-reading"
              : "text-small text-gray-mid m-0 max-w-reading"
          }
          data-testid={`partner-brand-kit-outcome-${result.outcome}`}
        >
          {result.message}
          {result.outcome !== "Succeeded" &&
            " You can continue and enter everything by hand."}
        </p>
      )}

      {!extracting && error && (
        <p
          className="text-small text-status-error m-0 max-w-reading"
          role="alert"
          data-testid="partner-brand-kit-error"
        >
          {error} You can continue and enter everything by hand.
        </p>
      )}
    </section>
  );
}

/** Marks extracted values for a field group whose own inputs live in a shared
 *  section component (Colors, Point of contact — both also mounted by the edit
 *  page, so their labels are not this wizard's to annotate). Rendered directly
 *  above the section it describes, on the step the fields live on, so the same
 *  per-field mark is still passed under the staff member's eye there. Each
 *  entry clears independently the moment its field is edited. */
function ExtractedGroupNotice({
  fields,
  testId,
}: {
  fields: { field: string; label: string }[];
  testId: string;
}) {
  if (fields.length === 0) return null;
  return (
    <div
      className="flex flex-wrap items-center gap-space-1 rounded-lg border border-border-light bg-white-warm p-space-3 text-small"
      data-testid={testId}
    >
      <span className="text-gray-mid">
        Read from the brand guide — check before going live:
      </span>
      {fields.map((f) => (
        <PartnerBrandExtractedMarker key={f.field} field={f.field} label={f.label} />
      ))}
    </div>
  );
}

const same = (a: string, b: string) =>
  a.trim().toLowerCase() === b.trim().toLowerCase();

/** A blank text field is one nobody has filled in, so it is safe to prefill.
 *  Anything already typed is left alone — always.
 *
 *  `supplied` is a value THIS WIZARD put there rather than the staff member —
 *  today only the personal-split branch, which field-copies the firm's site as
 *  a starting point before this step runs. That copy is a default, not intent,
 *  so a brand guide may replace it; the moment the staff member edits it the
 *  value stops matching and it is protected like anything else they typed. */
const untouchedText = (v: string, supplied?: string) =>
  v.trim() === "" || (supplied !== undefined && same(v, supplied));

/** Fonts and colors have no empty state: they start on the Phoenix defaults.
 *  Still sitting on the default — or on a value this wizard supplied — is this
 *  file's definition of untouched. */
const untouchedAgainst = (v: string, fallback: string, supplied?: string) =>
  untouchedText(v, supplied) || same(v, fallback);

export interface PartnerBrandWizardBodyProps {
  mode: "create" | "edit";
  /** Edit mode: the brand to edit. Ignored when creating. */
  brandId?: string;
  /** Launch context — pre-sets the owning broker (still changeable). */
  ownerBrokerId?: string | null;
  ownerBrokerName?: string | null;
  /** Launch context — pre-sets the owning brokerage (still changeable). */
  ownerBrokerageId?: string | null;
  ownerBrokerageName?: string | null;
  /** Dismissed without finishing — the route navigates away, the modal closes. */
  onCancel: () => void;
  /** Finished: went live (create), saved (edit), or ended on the kept-firm-site
   *  terminal. `brandId` is the row it ended on, null when nothing was created. */
  onCompleted: (brandId: string | null) => void;
  /** Reported on every change so a host can warn before closing. */
  onStateChange?: (state: PartnerBrandWizardState) => void;
}

export function PartnerBrandWizardBody({
  mode,
  brandId: editBrandId,
  ownerBrokerId,
  ownerBrokerName,
  ownerBrokerageId,
  ownerBrokerageName,
  onCancel,
  onCompleted,
  onStateChange,
}: PartnerBrandWizardBodyProps) {
  const navigate = useNavigate();
  const toast = useToast();
  const isEdit = mode === "edit";

  // Launch context is read ONCE, on mount; the owner stays editable on step 1.
  const [owner, setOwner] = useState<PartnerBrandOwner>(() => {
    if (ownerBrokerId)
      return { kind: "broker", id: ownerBrokerId, name: ownerBrokerName ?? null };
    if (ownerBrokerageId)
      return { kind: "brokerage", id: ownerBrokerageId, name: ownerBrokerageName ?? null };
    // No buyer-team context (D2a): a team's site is minted with the team and is
    // never drawn in this wizard.
    return OWNERLESS;
  });

  // The launch broker, captured once from the same context. Distinct from
  // `owner`, which the staff member can change: the fork is about the record
  // they launched FROM, so it must not move when they re-point the owner. Edit
  // mode never forks — the row already exists and has an owner.
  const [launchBroker] = useState<{ id: string; name: string | null } | null>(() =>
    !isEdit && ownerBrokerId ? { id: ownerBrokerId, name: ownerBrokerName ?? null } : null,
  );

  const [stepIndex, setStepIndex] = useState(0);
  // The persisted brand id — pre-set when editing, captured after step 1 when
  // creating. Null until then.
  const [brandId, setBrandId] = useState<string | null>(editBrandId ?? null);

  // Identity
  const [slug, setSlug] = useState("");
  const [referenceCode, setReferenceCode] = useState("");
  const [displayName, setDisplayName] = useState("");

  // Logo — synced from the logo control's returned brand. The two background
  // variants are URL-only here: they have no field on the create/update payload
  // (their upload/delete endpoints are their only writer), so nothing below
  // sends them and no partial update can clear them.
  const [logoMode, setLogoMode] = useState<PartnerLogoMode>("WordmarkText");
  const [logoAssetPath, setLogoAssetPath] = useState<string | null>(null);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [logoUrlOnLight, setLogoUrlOnLight] = useState<string | null>(null);
  const [logoUrlOnDark, setLogoUrlOnDark] = useState<string | null>(null);

  // Colors & fonts
  const [colors, setColors] = useState<PartnerBrandColors>(DEFAULT_COLORS);
  const [fontDisplay, setFontDisplay] = useState(DEFAULT_FONT_DISPLAY);
  const [fontBody, setFontBody] = useState(DEFAULT_FONT_BODY);
  const [colorMessages, setColorMessages] = useState<
    PartnerBrandValidationMessage[]
  >([]);

  // Hero & contact
  const [heroHeadline, setHeroHeadline] = useState("");
  const [heroSubcopy, setHeroSubcopy] = useState("");
  const [contactCity, setContactCity] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [contactWebsite, setContactWebsite] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  // On for a NEW site (the AI-disclosure wording is signed off). Resuming an
  // existing brand overwrites this from the loaded record below.
  const [analysisDocsEnabled, setAnalysisDocsEnabled] = useState(true);

  const [stepError, setStepError] = useState<string | null>(null);

  // ── Edit mode: hydrate once from the row being edited ─────────────────────
  const detail = usePartnerBrand(isEdit ? editBrandId : undefined);
  const [hydrated, setHydrated] = useState(!isEdit);
  useEffect(() => {
    if (!isEdit || hydrated || !detail.data) return;
    const b = detail.data;
    setSlug(b.slug);
    setReferenceCode(b.referenceCode);
    setDisplayName(b.displayName);
    setLogoMode(b.logoMode);
    setLogoAssetPath(b.logoAssetPath ?? null);
    setLogoUrl(b.logoUrl ?? null);
    setLogoUrlOnLight(b.logoUrlOnLight ?? null);
    setLogoUrlOnDark(b.logoUrlOnDark ?? null);
    setColors(b.colors);
    setFontDisplay(b.fontDisplay);
    setFontBody(b.fontBody);
    setHeroHeadline(b.heroHeadline ?? "");
    setHeroSubcopy(b.heroSubcopy ?? "");
    setContactCity(b.contactCity ?? "");
    setContactPhone(b.contactPhone ?? "");
    setContactWebsite(b.contactWebsite ?? "");
    setContactEmail(b.contactEmail ?? "");
    setAnalysisDocsEnabled(b.analysisDocsEnabled);
    setOwner(ownerFromBrand(b));
    setHydrated(true);
  }, [isEdit, hydrated, detail.data]);

  // ── Context fork ──────────────────────────────────────────────────────────
  // The ambiguity answer is a candidate firm id (or "personal only"); picking
  // one re-resolves the decision into the ordinary fork rather than ending it.
  const [firmChoice, setFirmChoice] = useState<string | null>(null);
  const [chosenFirmId, setChosenFirmId] = useState<string | null>(null);
  const decision = usePartnerBrandLaunchDecision(
    launchBroker?.id ?? null,
    launchBroker?.name,
    chosenFirmId,
  );
  const needsChoice = decisionNeedsChoice(decision);
  const [choiceOverride, setChoiceOverride] = useState<PartnerBrandContextChoice | null>(null);
  // Derived, not stored on arrival: the decision resolves asynchronously, and a
  // derived default is checked the moment it does without an effect to sync.
  const choice = choiceOverride ?? defaultChoiceFor(decision);
  const [decisionResolved, setDecisionResolved] = useState(false);
  // Terminal state for the "keep the firm's site" branch — nothing is created,
  // so the wizard ends here rather than walking on with a null brand id.
  const [keptFirmSite, setKeptFirmSite] = useState<{
    brandId: string;
    siteName: string;
    firmName: string;
  } | null>(null);

  const confirmInheritance = useConfirmPrequalSiteInheritance(launchBroker?.id ?? "");

  const showDecision = stepIndex === 0 && needsChoice && !decisionResolved;
  const showDecisionLoading = stepIndex === 0 && decision.mode === "loading";
  const showFirmPicker = showDecision && decision.mode === "ambiguous-firm";

  // ── Brand-guide pre-step ──────────────────────────────────────────────────
  // Create only: in edit mode every field is already populated, so a gate in
  // front of them would prefill nothing and cost a screen on every edit.
  const [brandKitResolved, setBrandKitResolved] = useState(isEdit);
  const [brandKitResult, setBrandKitResult] = useState<PartnerBrandKitResult | null>(
    null,
  );
  const [brandKitError, setBrandKitError] = useState<string | null>(null);
  const [extracting, setExtracting] = useState(false);
  /** Field keys prefilled from the guide and not yet reviewed. Session-only —
   *  never persisted, never sent. Colors are keyed `color.{token}`. */
  const [extracted, setExtracted] = useState<ReadonlySet<string>>(new Set());
  /** Values THIS WIZARD put in the form, keyed the same way. Only the
   *  personal-split branch writes here, field-copying the firm's site before
   *  the guide step runs. A guide may replace these — they are defaults the
   *  wizard chose, not anything the staff member typed. A ref, not state:
   *  nothing renders from it and it must not re-trigger the extract effect. */
  const supplied = useRef<Record<string, string>>({});

  // Rendered only once the fork (if any) is answered, so the two gates never
  // compete for step 1's body — and never while the decision is still loading,
  // which would flash this screen and then replace it with the fork.
  const showBrandKit =
    !isEdit &&
    stepIndex === 0 &&
    !showDecision &&
    !showDecisionLoading &&
    !brandKitResolved;

  const clearMark = (field: string) =>
    setExtracted((prev) => {
      if (!prev.has(field)) return prev;
      const next = new Set(prev);
      next.delete(field);
      return next;
    });

  const create = useCreatePartnerBrand();
  const update = useUpdatePartnerBrand(brandId ?? "");
  const setActive = useSetPartnerBrandActive(brandId ?? "");

  // Live slug uniqueness, from the list the wizard is already entitled to read
  // (same Partners.ManageBrands gate). Advisory: the server's 409 stays the
  // authority, this just says so before the step is submitted rather than after.
  const brands = usePartnerBrands();
  const slugTaken =
    !brandId && !!slug.trim() && !!brands.data?.some((b) => b.slug === slug.trim());

  const setColor = (key: keyof PartnerBrandColors, value: string) => {
    setColors((prev) => ({ ...prev, [key]: value }));
    clearMark(`color.${key}`);
  };

  /** Read the guide and prefill from it. NEVER overwrites a value already
   *  entered — a partially filled form is a set of decisions, and the display
   *  name in particular is the site's identity, not a neutral default. Every
   *  failure lands the staff member exactly where a skip would: on Identity,
   *  with nothing prefilled and a message saying why. No retry loop — Polly
   *  already retries the transport, and a second layer just multiplies the
   *  wait on a guide that was never going to parse. */
  const runExtract = async (file: File) => {
    setBrandKitError(null);
    setBrandKitResult(null);
    setStepError(null);
    setExtracting(true);
    try {
      const res = await partnerBrandsApi.extractBrandKit(file);
      setBrandKitResult(res);
      if (res.outcome !== "Succeeded") return;

      const d = res.draft;
      const marks = new Set<string>();

      const from = supplied.current;

      if (d.displayName && untouchedText(displayName, from.displayName)) {
        setDisplayName(d.displayName);
        marks.add("displayName");
      }
      setColors((prev) => {
        const next = { ...prev };
        for (const { key } of COLOR_FIELDS) {
          const value = d.colors[key];
          if (
            value &&
            untouchedAgainst(prev[key], DEFAULT_COLORS[key], from[`color.${key}`])
          ) {
            next[key] = value;
            marks.add(`color.${key}`);
          }
        }
        return next;
      });
      if (
        d.fontDisplay &&
        untouchedAgainst(fontDisplay, DEFAULT_FONT_DISPLAY, from.fontDisplay)
      ) {
        setFontDisplay(d.fontDisplay);
        marks.add("fontDisplay");
      }
      if (d.fontBody && untouchedAgainst(fontBody, DEFAULT_FONT_BODY, from.fontBody)) {
        setFontBody(d.fontBody);
        marks.add("fontBody");
      }
      if (d.heroHeadline && untouchedText(heroHeadline, from.heroHeadline)) {
        setHeroHeadline(d.heroHeadline);
        marks.add("heroHeadline");
      }
      if (d.heroSubcopy && untouchedText(heroSubcopy, from.heroSubcopy)) {
        setHeroSubcopy(d.heroSubcopy);
        marks.add("heroSubcopy");
      }
      if (d.contactCity && untouchedText(contactCity, from.contactCity)) {
        setContactCity(d.contactCity);
        marks.add("contactCity");
      }
      if (d.contactPhone && untouchedText(contactPhone, from.contactPhone)) {
        setContactPhone(d.contactPhone);
        marks.add("contactPhone");
      }
      if (d.contactWebsite && untouchedText(contactWebsite, from.contactWebsite)) {
        setContactWebsite(d.contactWebsite);
        marks.add("contactWebsite");
      }
      // MERGED, never replaced: a second guide fills only what is still
      // untouched, so replacing the set would drop the marks off values the
      // first guide supplied and nobody has reviewed — unmarking them on their
      // way to a live site.
      setExtracted((prev) => new Set([...prev, ...marks]));
    } catch (err) {
      setBrandKitError(extractErrorMessage(err));
    } finally {
      setExtracting(false);
    }
  };

  const handleLogoChange = (brand: PartnerBrand) => {
    setLogoMode(brand.logoMode);
    setLogoAssetPath(brand.logoAssetPath ?? null);
    setLogoUrl(brand.logoUrl ?? null);
    setLogoUrlOnLight(brand.logoUrlOnLight ?? null);
    setLogoUrlOnDark(brand.logoUrlOnDark ?? null);
  };

  const updatePayload = (): UpdatePartnerBrandRequest => ({
    displayName: displayName.trim(),
    referenceCode: referenceCode.trim(),
    logoMode,
    wordmarkText: null,
    logoAssetPath,
    colors,
    fontDisplay: fontDisplay.trim(),
    fontBody: fontBody.trim(),
    heroHeadline: heroHeadline.trim() || null,
    heroSubcopy: heroSubcopy.trim() || null,
    contactCity: contactCity.trim() || null,
    contactPhone: contactPhone.trim() || null,
    contactWebsite: contactWebsite.trim() || null,
    contactEmail: contactEmail.trim() || null,
    analysisDocsEnabled,
    // Carried on EVERY update too — the server assigns both owner columns from
    // the request, so omitting them mid-wizard would clear the launch owner.
    ...ownerIds(owner),
  });

  const createPayload = (): CreatePartnerBrandRequest => ({
    slug: slug.trim(),
    activate: false,
    ...updatePayload(),
  });

  // ── Dirty tracking, for a host that must warn before closing ──────────────
  // A snapshot comparison rather than a flag threaded through every setter: the
  // baseline is re-taken after each successful persist, so "dirty" means
  // genuinely unsaved. A created-but-abandoned draft counts too — it survives
  // the close, inactive, in the Partner Brands list.
  const snapshot = JSON.stringify({
    slug,
    referenceCode,
    displayName,
    logoAssetPath,
    colors,
    fontDisplay,
    fontBody,
    heroHeadline,
    heroSubcopy,
    contactCity,
    contactPhone,
    contactWebsite,
    contactEmail,
    analysisDocsEnabled,
    owner,
  });
  const baseline = useRef<string | null>(null);
  if (baseline.current === null && (!isEdit || hydrated)) baseline.current = snapshot;
  const [finished, setFinished] = useState(false);
  const draftBrandId = !isEdit && brandId && !finished ? brandId : null;
  const dirty =
    !finished && (snapshot !== (baseline.current ?? snapshot) || !!draftBrandId);

  // Declared before the reporting effect below, which names it as a dependency
  // (a dep array is evaluated during render, so a later `const` would be in its
  // temporal dead zone).
  const busy =
    create.isPending || update.isPending || confirmInheritance.isPending || extracting;

  useEffect(() => {
    onStateChange?.({ dirty, draftBrandId, busy });
  }, [dirty, draftBrandId, busy, onStateChange]);

  const step1Valid =
    !!slug.trim() && !!referenceCode.trim() && !!displayName.trim() && !slugTaken;
  const step3Valid = !!fontDisplay.trim() && !!fontBody.trim();
  // The split-off branch copies the firm's presentation fields, so it waits for
  // that read rather than silently starting from the Phoenix defaults.
  const awaitingFirmBrand =
    choice === "personal" &&
    decision.mode === "firm-has-site" &&
    decision.firmBrandLoading;
  const canAdvance = showDecisionLoading
    ? false
    : showFirmPicker
      ? // No default here: with several firms there is no safer one to guess at.
        !!firmChoice
      : showDecision
        ? !awaitingFirmBrand
        : // Skipping the brand guide is an ordinary path, not a failure — a
          // client with no brand guide must still get a site.
          showBrandKit
          ? true
          : stepIndex === 0
            ? step1Valid
            : stepIndex === 2
              ? step3Valid
              : true;

  // Persist whatever the current step owns before advancing. Step 1 creates the
  // row once (or updates it if the user went Back then Next again, and always in
  // edit mode); steps 3 & 4 update the captured row. Steps 2 (logo, its own
  // endpoint) & 5 don't persist here. Returns false + surfaces the error when
  // the write fails.
  const persistStep = async (): Promise<boolean> => {
    setStepError(null);
    try {
      if (stepIndex === 0) {
        if (!brandId) {
          const created = await create.mutateAsync(createPayload());
          setBrandId(created.id);
          setSlug(created.slug);
        } else {
          await update.mutateAsync(updatePayload());
        }
      } else if (stepIndex === 2 || stepIndex === 3) {
        await update.mutateAsync(updatePayload());
      }
      baseline.current = snapshot;
      return true;
    } catch (err) {
      const validation = asValidationResult(err);
      if (validation) {
        setColorMessages(validation.messages);
        setStepError("Fix the contrast errors before continuing.");
        return false;
      }
      const conflict = asSlugConflict(err);
      if (conflict) {
        setStepError(
          `The slug "${conflict.slug}" is already used by "${conflict.displayName}". Choose a different slug.`,
        );
        return false;
      }
      setStepError(extractErrorMessage(err));
      return false;
    }
  };

  /** Apply the fork's answer. Nothing is persisted here except on the
   *  keep-the-firm's-site branch, whose only write is the review annotation. */
  const resolveDecision = async () => {
    setStepError(null);

    // Ambiguous affiliation: the answer names WHICH firm, and the ordinary fork
    // is then asked about it. Declining every firm goes straight to personal.
    if (decision.mode === "ambiguous-firm") {
      if (firmChoice && firmChoice !== AMBIGUOUS_PERSONAL) {
        setChosenFirmId(firmChoice);
        setChoiceOverride(null);
        return;
      }
      if (launchBroker) {
        setOwner({ kind: "broker", id: launchBroker.id, name: launchBroker.name });
      }
      setDecisionResolved(true);
      return;
    }

    if (choice === "shared-firm" && decision.mode === "firm-no-site") {
      setOwner({ kind: "brokerage", id: decision.firm.id, name: decision.firm.name });
      setDecisionResolved(true);
      return;
    }

    if (choice === "use-existing" && decision.mode === "firm-has-site") {
      try {
        await confirmInheritance.mutateAsync();
      } catch (err) {
        // The panel claims a review was recorded, so it must not appear when
        // the record failed. The choice survives; the action is retryable.
        setStepError(extractErrorMessage(err));
        return;
      }
      setKeptFirmSite({
        brandId: decision.site.brandId,
        siteName: decision.site.displayName,
        firmName: decision.firm.name,
      });
      setFinished(true);
      return;
    }

    // Personal — this broker's own site, private from their firm. Field-COPY the
    // firm's presentation as a starting point (never a live link, and never the
    // identity fields: name, slug and reference code are this site's own).
    if (launchBroker) {
      setOwner({ kind: "broker", id: launchBroker.id, name: launchBroker.name });
    }
    if (decision.mode === "firm-has-site" && decision.firmBrand) {
      const b = decision.firmBrand;
      setColors(b.colors);
      setFontDisplay(b.fontDisplay);
      setFontBody(b.fontBody);
      setHeroHeadline(b.heroHeadline ?? "");
      setHeroSubcopy(b.heroSubcopy ?? "");
      setContactCity(b.contactCity ?? "");
      setContactPhone(b.contactPhone ?? "");
      setContactWebsite(b.contactWebsite ?? "");
      setContactEmail(b.contactEmail ?? "");

      // Remember what the copy supplied. The brand-guide step runs AFTER this,
      // so without this record every copied field would look "already filled"
      // and the guide would be discarded on exactly the path where the staff
      // member went out of their way to supply one.
      supplied.current = {
        fontDisplay: b.fontDisplay,
        fontBody: b.fontBody,
        heroHeadline: b.heroHeadline ?? "",
        heroSubcopy: b.heroSubcopy ?? "",
        contactCity: b.contactCity ?? "",
        contactPhone: b.contactPhone ?? "",
        contactWebsite: b.contactWebsite ?? "",
        ...Object.fromEntries(
          COLOR_FIELDS.map((f) => [`color.${f.key}`, b.colors[f.key]]),
        ),
      };
    }
    setDecisionResolved(true);
  };

  const goNext = async () => {
    if (showDecision) {
      await resolveDecision();
      return;
    }
    // The guide is already applied (or was skipped, or failed) — this only
    // moves past the gate, still within step 1. Nothing to persist.
    if (showBrandKit) {
      setBrandKitResolved(true);
      return;
    }
    if (await persistStep()) setStepIndex((i) => Math.min(i + 1, LAST));
  };
  // Re-opening either gate is only coherent before anything is persisted; once
  // a brand row exists it has an owner, and the owner field is the way to
  // change it. Step 1 without a fork or a guide keeps Back disabled, exactly as
  // before. Most recent gate first, so Back retraces the way in.
  const canReopenBrandKit =
    !isEdit &&
    stepIndex === 0 &&
    brandKitResolved &&
    !brandId &&
    !showDecision &&
    !showDecisionLoading;
  const canReopenDecision =
    stepIndex === 0 && needsChoice && decisionResolved && !brandId;
  const goBack = () => {
    setStepError(null);
    if (canReopenBrandKit) {
      setBrandKitResolved(false);
      return;
    }
    if (canReopenDecision) {
      setDecisionResolved(false);
      return;
    }
    setStepIndex((i) => Math.max(i - 1, 0));
  };

  const goLive = async () => {
    if (!brandId) return;
    setStepError(null);
    try {
      await setActive.mutateAsync(true);
      setFinished(true);
      toast.success("Partner brand is live.");
      onCompleted(brandId);
    } catch (err) {
      setStepError(extractErrorMessage(err));
    }
  };

  const saveEdit = async () => {
    if (!brandId) return;
    setStepError(null);
    try {
      await update.mutateAsync(updatePayload());
      baseline.current = snapshot;
      setFinished(true);
      toast.success("Partner brand saved.");
      onCompleted(brandId);
    } catch (err) {
      setStepError(extractErrorMessage(err));
    }
  };

  if (isEdit && detail.isLoading) {
    return <div className="text-small text-gray-mid">Loading partner brand…</div>;
  }
  if (isEdit && detail.error) {
    return (
      <div className="text-small text-status-error">{extractErrorMessage(detail.error)}</div>
    );
  }

  // Terminal: the firm's existing site was kept, so there is no draft to
  // configure and no step 5 to activate. Ending here beats leaving the wizard
  // running against a brand that was never created.
  if (keptFirmSite) {
    return (
      <section
        className="flex flex-col gap-space-3 bg-white-pure rounded-lg border border-border-light p-space-4 max-w-3xl"
        data-testid="partner-brand-kept-firm-site"
      >
        <p className="text-small text-onyx m-0 max-w-reading">
          {decision.mode === "firm-has-site" ? decision.brokerName : "This broker"} keeps
          using {keptFirmSite.firmName}&rsquo;s site,{" "}
          <strong>{keptFirmSite.siteName}</strong>. Nothing was created, and their
          completions stay visible to {keptFirmSite.firmName}.
        </p>
        <p className="text-caption text-gray-mid m-0 max-w-reading">
          Your review was recorded on the broker record. It is a note for the next person
          who looks — it changes nothing about which site resolves.
        </p>
        <div className="flex flex-wrap gap-space-2">
          <Button onClick={() => navigate(`/partners/brands/${keptFirmSite.brandId}`)}>
            Open {keptFirmSite.siteName}
          </Button>
          <Button variant="secondary" onClick={() => onCompleted(null)}>
            Done
          </Button>
        </div>
      </section>
    );
  }

  return (
    <div className="flex flex-col gap-space-4">
      <StatusStepper
        steps={STEPS}
        currentIndex={stepIndex}
        label={isEdit ? "Edit brand progress" : "New brand progress"}
      />

      <div className="flex flex-col gap-space-4 max-w-3xl">
        {/* ── Step 1 — the context fork, then Identity ──
            The gate replaces this step's body while it is open, so a brokerage
            launch and the standalone path render exactly what they always did. */}
        {showDecisionLoading && <PartnerBrandContextLoading />}

        {showDecision && !showDecisionLoading && (
          <PartnerBrandContextDecision
            decision={decision}
            value={choice}
            onChange={setChoiceOverride}
            firmChoice={firmChoice}
            onFirmChoiceChange={setFirmChoice}
          />
        )}

        {showBrandKit && (
          <BrandKitStep
            extracting={extracting}
            result={brandKitResult}
            error={brandKitError}
            onFile={runExtract}
          />
        )}

        {stepIndex === 0 && !showDecision && !showDecisionLoading && !showBrandKit && (
          <section className="flex flex-col gap-space-3">
            <h2 className="font-display font-bold text-h3 m-0">Identity</h2>
            <p className="text-caption text-gray-mid m-0">
              {isEdit
                ? "Edits are saved as you move through the steps."
                : brandId
                  ? "Draft saved — it stays inactive until you activate it on the final step."
                  : "Naming the brand creates an inactive draft (invisible on the funnel until you go live)."}
            </p>
            <PartnerBrandContextNotice decision={decision} />
            {decisionResolved &&
              (decision.mode === "firm-no-site" || decision.mode === "firm-has-site") && (
                <p
                  className="text-caption text-gray-mid m-0"
                  data-testid="partner-brand-chosen-scope"
                >
                  {choice === "shared-firm"
                    ? `Creating ${decision.firm.name}'s shared site — every broker there without their own uses it.`
                    : `Creating a personal site, private from ${decision.firm.name}.`}
                </p>
              )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-space-3">
              <div className="flex flex-col gap-space-1">
                <Label htmlFor="pb-slug">Slug</Label>
                <Input
                  id="pb-slug"
                  value={slug}
                  disabled={!!brandId}
                  placeholder="andiamo-ventures"
                  onChange={(e) => setSlug(toSlug(e.target.value))}
                />
                {slugTaken ? (
                  <p
                    className="text-caption text-status-error m-0"
                    data-testid="partner-brand-slug-taken"
                  >
                    /prequalify/{slug.trim()} is already taken. Choose a different slug.
                  </p>
                ) : (
                  <p className="text-caption text-gray-mid m-0">
                    {brandId
                      ? "Slug is fixed after create — issued links depend on it."
                      : "Lowercase letters, digits and hyphens. Public URL: /prequalify/{slug}."}
                  </p>
                )}
              </div>
              <div className="flex flex-col gap-space-1">
                <Label htmlFor="pb-ref">Reference code</Label>
                <Input
                  id="pb-ref"
                  value={referenceCode}
                  maxLength={8}
                  placeholder="AND"
                  onChange={(e) => setReferenceCode(e.target.value.toUpperCase())}
                />
                <p className="text-caption text-gray-mid m-0">
                  Up to 8 chars — stamped into the reference number
                  (PQ-AND-B-2026-0042).
                </p>
              </div>
              <div className="flex flex-col gap-space-1 sm:col-span-2">
                {/* The marker sits BESIDE the label, never inside it: it is a
                    status, not part of the field's name, and folding it in
                    would rewrite the input's accessible name. */}
                <div className="flex flex-wrap items-baseline">
                  <Label htmlFor="pb-name">Display name</Label>
                  {extracted.has("displayName") && (
                    <PartnerBrandExtractedMarker field="displayName" />
                  )}
                </div>
                <Input
                  id="pb-name"
                  value={displayName}
                  placeholder="Andiamo Ventures"
                  onChange={(e) => {
                    setDisplayName(e.target.value);
                    clearMark("displayName");
                  }}
                />
              </div>
            </div>
            <PartnerBrandOwnerSection value={owner} onChange={setOwner} />
          </section>
        )}

        {/* ── Step 2 — Logo ── */}
        {stepIndex === 1 && (
          <section className="flex flex-col gap-space-3">
            <h2 className="font-display font-bold text-h3 m-0">Logo</h2>
            <p className="text-caption text-gray-mid m-0">
              Upload a logo, or select Next to skip and keep the display-name
              wordmark.
            </p>
            <PartnerBrandLogoControl
              brandId={brandId ?? ""}
              logoUrl={logoUrl}
              logoUrlOnLight={logoUrlOnLight}
              logoUrlOnDark={logoUrlOnDark}
              onChange={handleLogoChange}
            />
          </section>
        )}

        {/* ── Step 3 — Colors & fonts ── */}
        {stepIndex === 2 && (
          <div className="flex flex-col gap-space-4">
            <ExtractedGroupNotice
              testId="partner-brand-kit-marks-colors"
              fields={COLOR_FIELDS.filter((f) =>
                extracted.has(`color.${f.key}`),
              ).map((f) => ({ field: `color.${f.key}`, label: f.label }))}
            />
            <PartnerBrandColorsSection
              colors={colors}
              onColorChange={setColor}
              messages={colorMessages}
              onMessagesChange={setColorMessages}
            />
            <section className="flex flex-col gap-space-3">
              <h2 className="font-display font-bold text-h3 m-0">Fonts</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-space-3">
                <div className="flex flex-col gap-space-1">
                  <div className="flex flex-wrap items-baseline">
                    <Label htmlFor="pb-font-display">Display font</Label>
                    {extracted.has("fontDisplay") && (
                      <PartnerBrandExtractedMarker field="fontDisplay" />
                    )}
                  </div>
                  <Input
                    id="pb-font-display"
                    value={fontDisplay}
                    onChange={(e) => {
                      setFontDisplay(e.target.value);
                      clearMark("fontDisplay");
                    }}
                  />
                </div>
                <div className="flex flex-col gap-space-1">
                  <div className="flex flex-wrap items-baseline">
                    <Label htmlFor="pb-font-body">Body font</Label>
                    {extracted.has("fontBody") && (
                      <PartnerBrandExtractedMarker field="fontBody" />
                    )}
                  </div>
                  <Input
                    id="pb-font-body"
                    value={fontBody}
                    onChange={(e) => {
                      setFontBody(e.target.value);
                      clearMark("fontBody");
                    }}
                  />
                </div>
              </div>
            </section>
          </div>
        )}

        {/* ── Step 4 — Hero copy & Point of contact ── */}
        {stepIndex === 3 && (
          <div className="flex flex-col gap-space-4">
            <section className="flex flex-col gap-space-3">
              <h2 className="font-display font-bold text-h3 m-0">Hero copy</h2>
              <p className="text-caption text-gray-mid m-0">
                Optional — falls back to the Phoenix defaults when blank.
              </p>
              <div className="flex flex-col gap-space-1">
                <div className="flex flex-wrap items-baseline">
                  <Label htmlFor="pb-hero-headline">Headline</Label>
                  {extracted.has("heroHeadline") && (
                    <PartnerBrandExtractedMarker field="heroHeadline" />
                  )}
                </div>
                <Input
                  id="pb-hero-headline"
                  value={heroHeadline}
                  onChange={(e) => {
                    setHeroHeadline(e.target.value);
                    clearMark("heroHeadline");
                  }}
                />
              </div>
              <div className="flex flex-col gap-space-1">
                <div className="flex flex-wrap items-baseline">
                  <Label htmlFor="pb-hero-subcopy">Subcopy</Label>
                  {extracted.has("heroSubcopy") && (
                    <PartnerBrandExtractedMarker field="heroSubcopy" />
                  )}
                </div>
                <Textarea
                  id="pb-hero-subcopy"
                  value={heroSubcopy}
                  rows={2}
                  onChange={(e) => {
                    setHeroSubcopy(e.target.value);
                    clearMark("heroSubcopy");
                  }}
                />
              </div>
            </section>
            <section className="flex flex-col gap-space-3">
              <h2 className="font-display font-bold text-h3 m-0">
                Point of contact
              </h2>
              <ExtractedGroupNotice
                testId="partner-brand-kit-marks-contact"
                fields={[
                  { field: "contactCity", label: "City" },
                  { field: "contactPhone", label: "Phone" },
                  { field: "contactWebsite", label: "Website" },
                ].filter((f) => extracted.has(f.field))}
              />
              <PartnerBrandContactSection
                city={contactCity}
                phone={contactPhone}
                website={contactWebsite}
                email={contactEmail}
                onCityChange={(v) => {
                  setContactCity(v);
                  clearMark("contactCity");
                }}
                onPhoneChange={(v) => {
                  setContactPhone(v);
                  clearMark("contactPhone");
                }}
                onWebsiteChange={(v) => {
                  setContactWebsite(v);
                  clearMark("contactWebsite");
                }}
                // No clearMark: the brand-kit extractor does not read an email
                // off a guide, so this field can never carry an extraction mark.
                onEmailChange={setContactEmail}
              />
            </section>
            <label className="flex items-center gap-space-2 text-small">
              <input
                type="checkbox"
                checked={analysisDocsEnabled}
                onChange={(e) => setAnalysisDocsEnabled(e.target.checked)}
              />
              Enable private analysis documents for this partner's
              prequalification submissions
            </label>
          </div>
        )}

        {/* ── Step 5 — Review & activate ── */}
        {stepIndex === 4 && (
          <section className="flex flex-col gap-space-4">
            <h2 className="font-display font-bold text-h3 m-0">
              {isEdit ? "Review & save" : "Review & activate"}
            </h2>
            <p className="text-small text-gray-mid m-0 max-w-reading">
              {isEdit
                ? `Saving keeps /prequalify/${slug || "{slug}"} on this skin.`
                : `The brand is saved as an inactive draft. Go live to make /prequalify/${slug || "{slug}"} resolve to this skin.`}
            </p>
            <div className="flex flex-col gap-space-3 bg-white-pure rounded-lg border border-border-light p-space-4">
              <ReviewRow label="Slug">
                <span className="font-mono">{slug || "—"}</span>
              </ReviewRow>
              <ReviewRow label="Reference code">
                <span className="font-mono">{referenceCode || "—"}</span>
              </ReviewRow>
              <ReviewRow label="Display name">{displayName || "—"}</ReviewRow>
              <ReviewRow label="Owner">
                {owner.kind === "none" || !owner.id ? (
                  <span className="text-gray-mid">
                    Unowned — general referral partner
                  </span>
                ) : (
                  <>
                    {owner.kind === "broker" ? "Broker" : "Brokerage"}
                    {owner.name ? ` — ${owner.name}` : ""}
                  </>
                )}
              </ReviewRow>
              <ReviewRow label="Logo">
                {logoUrl ? (
                  <img
                    src={logoUrl}
                    alt="Logo preview"
                    className="h-12 w-auto object-contain"
                  />
                ) : (
                  <span className="text-gray-mid">
                    Wordmark — {displayName || slug || "—"}
                  </span>
                )}
              </ReviewRow>
              {(logoUrlOnLight || logoUrlOnDark) && (
                <ReviewRow label="Logo variants">
                  <span className="inline-flex flex-wrap items-center gap-space-2">
                    {logoUrlOnLight && (
                      <span className="inline-flex items-center gap-space-1">
                        <img
                          src={logoUrlOnLight}
                          alt="On light backgrounds"
                          className="h-8 w-auto object-contain bg-white-pure border border-border-light rounded-sm p-space-1"
                        />
                        <span className="text-caption text-gray-mid">On light</span>
                      </span>
                    )}
                    {logoUrlOnDark && (
                      <span className="inline-flex items-center gap-space-1">
                        <img
                          src={logoUrlOnDark}
                          alt="On dark backgrounds"
                          className="h-8 w-auto object-contain bg-onyx border border-onyx rounded-sm p-space-1"
                        />
                        <span className="text-caption text-gray-mid">On dark</span>
                      </span>
                    )}
                  </span>
                </ReviewRow>
              )}
              <ReviewRow label="Colors">
                <div className="flex flex-wrap gap-space-2">
                  {COLOR_FIELDS.map((f) => (
                    <span
                      key={f.key}
                      className="inline-flex items-center gap-space-1"
                      title={f.label}
                    >
                      <span
                        className="h-5 w-5 rounded-sm border border-border-light"
                        style={{ backgroundColor: swatch(colors[f.key]) }}
                        aria-hidden
                      />
                      <span className="text-caption font-mono">
                        {colors[f.key]}
                      </span>
                    </span>
                  ))}
                </div>
              </ReviewRow>
              <ReviewRow label="Fonts">
                {fontDisplay} / {fontBody}
              </ReviewRow>
              <ReviewRow label="Hero">
                {heroHeadline || heroSubcopy ? (
                  <span>
                    {heroHeadline || "—"}
                    {heroSubcopy && (
                      <span className="text-gray-mid"> · {heroSubcopy}</span>
                    )}
                  </span>
                ) : (
                  <span className="text-gray-mid">—</span>
                )}
              </ReviewRow>
              <ReviewRow label="Contact">
                {[contactCity, contactPhone, contactWebsite, contactEmail].some(Boolean) ? (
                  [contactCity, contactPhone, contactWebsite, contactEmail]
                    .filter(Boolean)
                    .join(" · ")
                ) : (
                  <span className="text-gray-mid">—</span>
                )}
              </ReviewRow>
              <ReviewRow label="Analysis docs">
                <Badge status={analysisDocsEnabled ? "finalized" : "archived"}>
                  {analysisDocsEnabled ? "Enabled" : "Off"}
                </Badge>
              </ReviewRow>
            </div>
          </section>
        )}

        {stepError && (
          <p className="text-small text-status-error m-0" role="alert">
            {stepError}
          </p>
        )}

        <div className="flex items-center justify-between gap-space-2 pt-space-2 border-t border-border-light">
          <Button type="button" variant="text" onClick={onCancel}>
            Cancel
          </Button>
          <div className="flex gap-space-2">
            <Button
              type="button"
              variant="secondary"
              onClick={goBack}
              disabled={
                (stepIndex === 0 && !canReopenDecision && !canReopenBrandKit) || busy
              }
            >
              Back
            </Button>
            {stepIndex < LAST ? (
              <Button
                type="button"
                onClick={goNext}
                loading={busy}
                disabled={!canAdvance}
              >
                {extracting
                  ? "Reading…"
                  : busy
                    ? "Saving…"
                    : showBrandKit
                      ? // Named for what it does from here: with nothing read,
                        // moving on IS skipping, and saying so beats a bare
                        // "Next" that hides the choice being made.
                        brandKitResult?.outcome === "Succeeded"
                        ? "Continue"
                        : "Skip"
                      : showDecision
                        ? "Continue"
                        : "Next"}
              </Button>
            ) : isEdit ? (
              <Button type="button" onClick={saveEdit} disabled={!brandId || busy}>
                {busy ? "Saving…" : "Save & close"}
              </Button>
            ) : (
              <Button
                type="button"
                onClick={goLive}
                disabled={!brandId || setActive.isPending}
              >
                {setActive.isPending ? "Activating…" : "Go live"}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
